import React from 'react';
import { connect } from 'react-redux';
import { compose, withHandlers, withProps } from 'recompose';
import { withTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import keyGenerator from 'utils/keys';
import {
  actions as shipmentsActions,
  selectors as shipmentsSelectors,
} from 'state/shipments';
import { selectors as orderSelector } from 'state/order';
import { selectors as uiSelectors } from 'state/ui';

import {
  formatters as dateAndTimeFormatters,
  methods as dateAndTimeMethods,
} from 'utils/dateAndTimes';
import { CALENDAR_MODIFIERS } from 'components/Calendar/container';

import DatePicker from 'components/form/DatePicker/component';

const PICKUP = 'pickup';

const enhance = compose(
  connect(
    (state, { shipmentKey, stopType }) => {
      const loadsInForm =
        orderSelector.maxNumberOfItemsInLoadsBetweenShipments(state);
      const orderType = orderSelector.getTypeOfOrder(state);
      const { min, max } = shipmentsSelectors.getMinAndMaxForDates(
        state,
        shipmentKey,
        stopType,
        'time_from',
        loadsInForm,
        orderType
      );

      const isPickup = stopType === PICKUP;

      return {
        value: shipmentsSelectors.getShipmentDate(state, shipmentKey, stopType)
          ? new Date(
              shipmentsSelectors.getShipmentDate(state, shipmentKey, stopType)
            )
          : undefined,
        minDate:
          isPickup &&
          dateAndTimeMethods.canBeToday(
            shipmentsSelectors.getShipmentTimezone(state, shipmentKey, 'pickup')
          )
            ? moment()
                .tz(
                  shipmentsSelectors.getShipmentTimezone(
                    state,
                    shipmentKey,
                    'pickup'
                  )
                )
                .toDate()
            : min.value.toDate(),
        maxDate: stopType !== PICKUP ? max.value.toDate() : undefined,
        disableWeekends: false,
        disabledDays: null,
        calendarModifiers: isPickup ? [CALENDAR_MODIFIERS.higherPrice] : [],
        warnings: shipmentsSelectors.getShipmentTimesWarnings(
          state,
          shipmentKey,
          stopType
        ),
        errors: shipmentsSelectors.getShipmentTimesErrors(
          state,
          shipmentKey,
          stopType
        ),
        tooltipText: uiSelectors.getFormTooltip(
          state,
          shipmentKey + '_' + stopType
        ),
      };
    },
    {
      changeDate: shipmentsActions.changeDate,
    }
  ),
  withTranslation('dates'),
  withProps(
    ({
      value,
      minDate,
      maxDate,
      shipmentKey,
      stopType,
      t,
      warnings,
      errors,
      calendarModifiers,
    }) => {
      return {
        id: keyGenerator.date({ shipmentKey, stopType }),
        renderValue:
          value &&
          dateAndTimeFormatters.getHumanReadableFormattedDate({ value, t }),
        minDate,
        maxDate,
        label: t(`label.${stopType}`),
        fromWarning: warnings[keyGenerator.date({ shipmentKey, stopType })],
        error: errors[keyGenerator.date({ shipmentKey, stopType })],
        calendarModifiers,
      };
    }
  ),
  withProps(({ error }) => {
    return {
      errorLabel: typeof error === 'string' ? error : 'errorDiffDays',
    };
  }),
  withHandlers(() => {
    let responsiveModalRef = React.createRef();
    return {
      responsiveModalRef: () => (ref) => {
        responsiveModalRef = ref;
      },
      handleChange:
        ({ changeDate, stopType, shipmentKey, maxDate, minDate }) =>
        (value) => {
          if (
            (dateAndTimeMethods.areTheSameDate(value, minDate) ||
              value >= minDate) &&
            (stopType === PICKUP ||
              dateAndTimeMethods.areTheSameDate(value, maxDate) ||
              value <= maxDate)
          ) {
            changeDate({ value, stopType, shipmentKey });
            responsiveModalRef.close();
          }
        },
    };
  })
);

export default enhance(DatePicker);
